import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { 
  DollarSign, TrendingUp, TrendingDown, Users, Ticket, Gift,
  Calendar, ArrowUpRight, ArrowDownRight, 
  Download, Filter, PieChart, BarChart3, Activity
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { PartnerLayout } from '@/components/PartnerLayout';
import { DateRangePicker } from '@/components/DateRangePicker';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
  AreaChart,
  Area
} from 'recharts';

const COLORS = ['#6366f1', '#8b5cf6', '#a855f7', '#c084fc'];

const MetricCard = ({ title, value, change, changeType, icon: Icon, subtitle, trend }) => {
  const isPositive = changeType === 'positive';
  const isNeutral = changeType === 'neutral';
  
  return (
    <Card className="relative overflow-hidden border-0 shadow-md">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold text-foreground">{value}</p>
            {subtitle && (
              <p className="text-xs text-muted-foreground">{subtitle}</p>
            )}
            {change !== null && change !== undefined && (
              <div className={`flex items-center gap-1 text-sm ${
                isNeutral ? 'text-gray-500' :
                isPositive ? 'text-green-600' : 'text-red-600'
              }`}>
                {!isNeutral && (isPositive ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />)}
                <span>{change}% vs last period</span>
              </div>
            )}
          </div>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
            isPositive ? 'bg-green-100 text-green-600 dark:bg-green-950 dark:text-green-400' : 
            'bg-indigo-100 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-400'
          }`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
        {trend && trend.length > 0 && (
          <div className="mt-4 h-12">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trend}>
                <Area 
                  type="monotone" 
                  dataKey="value" 
                  stroke={isPositive ? '#22c55e' : '#6366f1'} 
                  fill={isPositive ? '#22c55e20' : '#6366f120'} 
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const RevenueChart = ({ data, period, onPeriodChange }) => {
  return (
    <Card className="border-0 shadow-md">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle className="text-lg font-semibold">Revenue Overview</CardTitle>
          <CardDescription>Daily revenue for the selected period</CardDescription>
        </div>
        <Select value={period} onValueChange={onPeriodChange}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="14">Last 14 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis 
                dataKey="label" 
                tick={{ fontSize: 12 }} 
                className="text-muted-foreground"
              />
              <YAxis 
                tick={{ fontSize: 12 }} 
                tickFormatter={(value) => `$${value}`}
                className="text-muted-foreground"
              />
              <Tooltip 
                formatter={(value) => [`$${value.toFixed(2)}`, 'Revenue']}
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--card))', 
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }}
              />
              <Bar 
                dataKey="value" 
                fill="#6366f1" 
                radius={[4, 4, 0, 0]}
                className="hover:opacity-80 transition-opacity"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

const SalesBreakdownChart = ({ data }) => {
  const chartData = [
    { name: 'Tickets', value: data?.ticket_sales?.count || 0, revenue: data?.ticket_sales?.revenue || 0 },
    { name: 'Experiences', value: data?.experience_sales?.count || 0, revenue: data?.experience_sales?.revenue || 0 }
  ];

  return (
    <Card className="border-0 shadow-md">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-semibold">Sales Breakdown</CardTitle>
            <CardDescription>Tickets vs Experiences</CardDescription>
          </div>
          <PieChart className="w-5 h-5 text-muted-foreground" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <RechartsPie>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={5}
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name, props) => [
                  `${value} sold ($${props.payload.revenue.toFixed(2)})`, 
                  name
                ]}
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--card))', 
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }}
              />
            </RechartsPie>
          </ResponsiveContainer>
        </div>
        <div className="flex justify-center gap-6 mt-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-indigo-500" />
            <span className="text-sm text-muted-foreground">Tickets ({data?.ticket_sales?.percentage || 0}%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-purple-500" />
            <span className="text-sm text-muted-foreground">Experiences ({data?.experience_sales?.percentage || 0}%)</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const RevenueByVenueChart = ({ data }) => {
  return (
    <Card className="border-0 shadow-md">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-semibold">Revenue by Venue</CardTitle>
            <CardDescription>Performance across your venues</CardDescription>
          </div>
          <BarChart3 className="w-5 h-5 text-muted-foreground" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ left: 20, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis type="number" tickFormatter={(value) => `$${value}`} />
              <YAxis 
                type="category" 
                dataKey="venue" 
                tick={{ fontSize: 11 }} 
                width={120}
              />
              <Tooltip 
                formatter={(value) => [`$${value.toFixed(2)}`, 'Revenue']}
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--card))', 
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }}
              />
              <Bar dataKey="revenue" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

const TopPerformers = ({ items, type }) => {
  return (
    <Card className="border-0 shadow-md">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold">Top {type}</CardTitle>
          <Activity className="w-5 h-5 text-muted-foreground" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {items.map((item, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                i === 0 ? 'bg-yellow-400 text-yellow-900' :
                i === 1 ? 'bg-gray-300 text-gray-700' :
                i === 2 ? 'bg-orange-400 text-orange-900' :
                'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
              }`}>
                {i + 1}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground truncate">{item.name}</p>
                <p className="text-xs text-muted-foreground">{item.tickets} tickets sold</p>
              </div>
              <div className="text-right">
                <p className="font-bold text-foreground">${item.revenue?.toFixed(0) || 0}</p>
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <p className="text-center text-muted-foreground py-4">No events yet</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const PerformanceSummary = ({ data }) => {
  return (
    <Card className="border-0 shadow-md">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-semibold">Performance Summary</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-950/20 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="font-medium text-foreground">Conversion Rate</p>
              <p className="text-xs text-muted-foreground">Reservations to tickets</p>
            </div>
          </div>
          <span className="text-2xl font-bold text-green-600">{data?.conversion_rate || 0}%</span>
        </div>
        <div className="flex items-center justify-between p-4 bg-indigo-50 dark:bg-indigo-950/20 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="font-medium text-foreground">Total Reservations</p>
              <p className="text-xs text-muted-foreground">All time</p>
            </div>
          </div>
          <span className="text-2xl font-bold text-indigo-600">{data?.total_reservations || 0}</span>
        </div>
        <div className="flex items-center justify-between p-4 bg-purple-50 dark:bg-purple-950/20 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
              <Gift className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="font-medium text-foreground">Active Experiences</p>
              <p className="text-xs text-muted-foreground">Vouchers available</p>
            </div>
          </div>
          <span className="text-2xl font-bold text-purple-600">{data?.total_experiences || 0}</span>
        </div>
      </CardContent>
    </Card>
  );
};

const PartnerAnalytics = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isPartner, user, loading: authLoading, refreshUser } = useAuth();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [salesData, setSalesData] = useState(null);
  const [reservationData, setReservationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('7');
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const init = async () => {
      if (authLoading) return;
      
      if (!isAuthenticated) {
        navigate('/auth');
        return;
      }
      
      if (!user) {
        try {
          await refreshUser();
        } catch (error) {
          navigate('/auth');
          return;
        }
      }
      
      if (!isPartner) {
        navigate('/dashboard');
        return;
      }
      
      fetchAnalytics();
    };
    
    init();
  }, [isAuthenticated, isPartner, authLoading, user]);

  useEffect(() => {
    if (isAuthenticated && isPartner) {
      fetchRevenueData();
    }
  }, [period, isAuthenticated, isPartner]);

  // Refetch when date range changes
  useEffect(() => {
    if (isAuthenticated && isPartner && dateRange?.from) {
      // Calculate days from date range
      const days = dateRange.to 
        ? Math.ceil((dateRange.to - dateRange.from) / (1000 * 60 * 60 * 24)) + 1
        : 30;
      if (days !== parseInt(period)) {
        setPeriod(days.toString());
      }
    }
  }, [dateRange]);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      let url = '/partner/revenue/export';
      const params = new URLSearchParams();
      
      if (dateRange?.from) {
        params.append('start_date', format(dateRange.from, 'yyyy-MM-dd'));
      }
      if (dateRange?.to) {
        params.append('end_date', format(dateRange.to, 'yyyy-MM-dd'));
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await api.get(url, { responseType: 'blob' });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      let filename = `analytics_export_${format(new Date(), 'yyyyMMdd')}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Analytics data exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export analytics data');
    } finally {
      setExporting(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const [summaryRes, salesRes, reservationRes] = await Promise.all([
        api.get('/partner/analytics/summary'),
        api.get('/partner/analytics/sales-by-type'),
        api.get('/partner/analytics/reservations')
      ]);
      
      setAnalyticsData(summaryRes.data);
      setSalesData(salesRes.data);
      setReservationData(reservationRes.data);
      
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRevenueData = async () => {
    try {
      const response = await api.get(`/partner/analytics/revenue-over-time?days=${period}`);
      
      // Transform data for chart
      const chartData = response.data.last_7_days?.map(item => ({
        label: new Date(item.date).toLocaleDateString('en-US', { weekday: 'short' }),
        value: item.revenue,
        date: item.date
      })) || [];
      
      setRevenueData(chartData);
    } catch (error) {
      console.error('Failed to fetch revenue data:', error);
      setRevenueData([]);
    }
  };

  if (!isAuthenticated || !isPartner) return null;

  // Always use real data, no more demo/sample data
  const displaySalesData = salesData || { event_sales: 0, experience_sales: 0 };
  const displayVenueData = analyticsData?.revenue_by_venue || [];
  
  const displayMetrics = {
    gross_sales: analyticsData?.gross_sales || 0,
    tickets_sold: analyticsData?.tickets_sold || 0,
    experiences_sold: analyticsData?.experiences_sold || 0,
    total_events: analyticsData?.total_events || 0,
    total_reservations: analyticsData?.total_reservations || 0,
    total_experiences: analyticsData?.total_experiences || 0,
    conversion_rate: analyticsData?.conversion_rate || 0,
    top_events: analyticsData?.top_events || []
  };

  // Generate trend data for metric cards
  const revenueTrend = [40, 60, 45, 80, 55, 90, 75].map(v => ({ value: v }));
  const ticketsTrend = [30, 50, 40, 70, 60, 85, 70].map(v => ({ value: v }));

  return (
    <PartnerLayout 
      title="Analytics Dashboard" 
      subtitle="Track your performance, revenue, and sales metrics"
      actions={
        <div className="flex gap-2">
          <DateRangePicker
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
          />
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleExportCSV}
            disabled={exporting}
            data-testid="export-csv-btn"
          >
            {exporting ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export
          </Button>
        </div>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Key Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <MetricCard
              title="Gross Sales"
              value={`$${(displayMetrics?.gross_sales || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              change={12.5}
              changeType="positive"
              icon={DollarSign}
              subtitle="Tickets + Experiences"
              trend={revenueTrend}
            />
            <MetricCard
              title="Tickets Sold"
              value={displayMetrics?.tickets_sold || 0}
              change={8.2}
              changeType="positive"
              icon={Ticket}
              subtitle={`${displayMetrics?.total_events || 0} active events`}
              trend={ticketsTrend}
            />
            <MetricCard
              title="Experiences Sold"
              value={displayMetrics?.experiences_sold || 0}
              change={15.3}
              changeType="positive"
              icon={Gift}
              subtitle={`${displayMetrics?.total_experiences || 0} vouchers available`}
            />
            <MetricCard
              title="Total Venues"
              value={analyticsData?.total_venues || 0}
              change={null}
              changeType="neutral"
              icon={Calendar}
              subtitle="Managed locations"
            />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <RevenueChart 
                data={revenueData || []} 
                period={period}
                onPeriodChange={setPeriod}
              />
            </div>
            <SalesBreakdownChart data={displaySalesData} />
          </div>

          {/* Second Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <RevenueByVenueChart data={displayVenueData} />
            <TopPerformers 
              items={displayMetrics?.top_events || []} 
              type="Events" 
            />
            <PerformanceSummary data={displayMetrics} />
          </div>

          {/* Reservation Analytics Section */}
          {reservationData && (
            <div className="space-y-6" data-testid="reservation-analytics-section">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Calendar className="w-5 h-5 text-indigo-600" />
                Reservation Analytics
              </h2>
              
              {/* Reservation Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-indigo-600">{reservationData.total_reservations || 0}</p>
                      <p className="text-sm text-muted-foreground">Total Reservations</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-green-600">{reservationData.reservations_this_week || 0}</p>
                      <p className="text-sm text-muted-foreground">This Week</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-center">
                      <p className="text-3xl font-bold">{reservationData.average_party_size || 0}</p>
                      <p className="text-sm text-muted-foreground">Avg Party Size</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className={reservationData.no_show_rate > 10 ? 'border-red-200' : ''}>
                  <CardContent className="p-4">
                    <div className="text-center">
                      <p className={`text-3xl font-bold ${reservationData.no_show_rate > 10 ? 'text-red-600' : 'text-gray-600'}`}>
                        {reservationData.no_show_rate || 0}%
                      </p>
                      <p className="text-sm text-muted-foreground">No-Show Rate</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Busiest Times & Day Distribution */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Busiest Times */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Busiest Times</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {reservationData.busiest_times?.length > 0 ? (
                      <div className="space-y-2">
                        {reservationData.busiest_times.slice(0, 6).map((t, idx) => (
                          <div key={idx} className="flex items-center justify-between">
                            <span className="text-sm font-medium">{t.time}</span>
                            <div className="flex items-center gap-2">
                              <div 
                                className="h-2 bg-indigo-500 rounded-full" 
                                style={{ width: `${Math.min(100, (t.count / Math.max(...reservationData.busiest_times.map(x => x.count))) * 100)}px` }}
                              />
                              <span className="text-sm text-muted-foreground w-8">{t.count}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">No data yet</p>
                    )}
                  </CardContent>
                </Card>

                {/* Day Distribution */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Reservations by Day</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {reservationData.reservations_by_day?.length > 0 ? (
                      <div className="space-y-2">
                        {reservationData.reservations_by_day.map((d, idx) => (
                          <div key={idx} className="flex items-center justify-between">
                            <span className="text-sm font-medium w-20">{d.day.substring(0, 3)}</span>
                            <div className="flex items-center gap-2 flex-1">
                              <div 
                                className="h-2 bg-green-500 rounded-full" 
                                style={{ width: `${Math.min(100, (d.count / Math.max(...reservationData.reservations_by_day.map(x => x.count), 1)) * 100)}%` }}
                              />
                              <span className="text-sm text-muted-foreground w-8">{d.count}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">No data yet</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Status Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Reservation Status Overview</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {reservationData.status_breakdown?.map((s, idx) => (
                      <div key={idx} className="text-center p-3 bg-muted/50 rounded-lg">
                        <p className="text-2xl font-bold">{s.count}</p>
                        <p className="text-xs text-muted-foreground">{s.status}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      )}
    </PartnerLayout>
  );
};

export default PartnerAnalytics;
